import { config } from "config";
import mixpanel from "mixpanel-browser";

// Initialize Mixpanel
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
if (!MIXPANEL_TOKEN) {
  console.warn("Mixpanel: REACT_APP_MIXPANEL_TOKEN is not set");
} else if (config.APP_ENV === "Development") {
  console.log("Mixpanel: skipped in Development");
} else {
  try {
    console.log("Initializing Mixpanel..."); // Debug log
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: true, // Force debug mode to see all logs
      track_pageview: true,
      persistence: "localStorage",
      api_host: "https://api-eu.mixpanel.com", // Using EU endpoint for GDPR compliance
    });
    console.log("Mixpanel initialized successfully"); // Debug log

    // Test event to verify tracking
    // mixpanel.track('Test Event', {
    //   test: true,
    //   timestamp: Date.now()
    // });
  } catch (error) {
    console.error("Error initializing Mixpanel:", error);
  }
}

// Authentication Events
export const trackButtonLogin = (properties: {
  email: string;
  phone_exists: boolean;
  otp_verification: boolean;
  screen: string;
}) => {
  mixpanel.track("BUTTON_LOGIN", properties);
};

export const trackButtonLogout = (properties: {
  screen: string;
  timestamp: number;
}) => {
  mixpanel.track("BUTTON_LOGOUT", properties);
};

export const trackButtonForgotPassword = (properties: {
  method: string;
  screen: string;
}) => {
  mixpanel.track("BUTTON_FORGOT_PASSWORD", properties);
};

export const trackButtonVerifyOTP = (properties: {
  error: string;
  screen: string;
  screen_index: number;
}) => {
  mixpanel.track("BUTTON_VERIFY_OTP", properties);
};

export const trackButtonResendOTP = (properties: {
  screen: string;
  screen_index: number;
}) => {
  mixpanel.track("BUTTON_RESEND_OTP", properties);
};

export const trackActionLogin = (properties: {
  login_result: string;
  error_message?: string;
  user_id: string;
}) => {
  mixpanel.track("ACTION_LOGIN", properties);
};

export const trackActionSignup = (properties: {
  signup_result: string;
  error_message?: string;
  first_name: string;
  email: string;
  last_name: string;
  title: string;
  phone: string;
  country_code: string;
  country: string;
}) => {
  mixpanel.track("ACTION_SIGNUP", properties);
};

export const trackActionLogout = (properties: {
  screen: string;
  timestamp: number;
}) => {
  mixpanel.track("ACTION_LOGOUT", properties);
};

// API Events
export const trackApiLogin = (properties: {
  phone_exists: boolean;
  otp_verification: boolean;
  screen: string;
}) => {
  mixpanel.track("API_LOGIN", properties);
};

export const trackApiSignup = (properties: {
  status: string;
  screen: string;
}) => {
  mixpanel.track("API_SIGNUP", properties);
};

export const trackApiVerifyOTP = (properties: {
  status: string;
  screen: string;
}) => {
  mixpanel.track("API_VERIFY_OTP", properties);
};

export const trackApiForgotPassword = (properties: {
  method: string;
  screen: string;
}) => {
  mixpanel.track("API_FORGOT_PASSWORD", properties);
};

// Screen Events
export const trackScreenLogin = (properties: { timestamp: number }) => {
  mixpanel.track("SCREEN_LOGIN", properties);
};

export const trackScreenSignup = (properties: { timestamp: number }) => {
  mixpanel.track("SCREEN_SIGNUP", properties);
};

export const trackScreenOTP = (properties: { timestamp: number }) => {
  mixpanel.track("SCREEN_OTP", properties);
};

export const trackScreenProfile = (properties: {
  timestamp: number;
  user_id: string;
  user_email: string;
}) => {
  mixpanel.track("SCREEN_PROFILE", properties);
};

export const trackScreenSettings = (properties: { user_id: string }) => {
  mixpanel.track("SCREEN_SETTINGS", properties);
};

// Error Events
export const trackErrorLogin = (properties: {
  error_message: string;
  screen: string;
}) => {
  mixpanel.track("ERROR_LOGIN", properties);
};

export const trackErrorSignup = (properties: {
  error_message: string;
  screen: string;
}) => {
  mixpanel.track("ERROR_SIGNUP", properties);
};

export const trackErrorOTP = (properties: {
  error: string;
  screen: string;
  screen_index: number;
}) => {
  mixpanel.track("ERROR_OTP", properties);
};

// Trip Events
export const trackScreenTripBooking = (properties: { timestamp: number }) => {
  mixpanel.track("SCREEN_TRIP_BOOKING", properties);
};

export const trackButtonBookTrip = (properties: { timestamp: number }) => {
  mixpanel.track("BUTTON_BOOK_TRIP", properties);
};

export const trackScreenTripBooked = (properties: { timestamp: number }) => {
  mixpanel.track("SCREEN_TRIP_BOOKED", properties);
};

export const trackActionTripBooked = (properties: {
  vehicle_class: string;
  vehicle_name: string;
  price: number;
  discount: number;
  max_bags: number;
  max_persons: number;
  screen: string;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("ACTION_TRIP_BOOKED", properties);
};

// Navigation Events
export const trackNavToLogin = (properties: {
  from_screen: string;
  to_screen: string;
  screen_index: number;
}) => {
  mixpanel.track("NAV_TO_LOGIN", properties);
};

export const trackNavToSignup = (properties: {
  from_screen: string;
  to_screen: string;
  screen_index: number;
}) => {
  mixpanel.track("NAV_TO_SIGNUP", properties);
};

export const trackNavToOTP = (properties: {
  from_screen: string;
  to_screen: string;
  screen_index: number;
}) => {
  mixpanel.track("NAV_TO_OTP", properties);
};

export const trackNavToHome = (properties: {
  from_screen: string;
  to_screen: string;
}) => {
  mixpanel.track("NAV_TO_HOME", properties);
};

export const trackNavToProfile = (properties: {
  from_screen: string;
  to_screen: string;
}) => {
  mixpanel.track("NAV_TO_PROFILE", properties);
};

export const trackNavToTrips = (properties: {
  from_screen: string;
  to_screen: string;
  booking_type: string;
  has_flight_number: boolean;
  has_chauffeur_note: boolean;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("NAV_TO_TRIPS", properties);
};

// Profile Events
export const trackActionEditProfile = (properties: {
  status: string;
  screen: string;
}) => {
  mixpanel.track("ACTION_EDIT_PROFILE", properties);
};

export const trackButtonEditProfile = (properties: { screen: string }) => {
  mixpanel.track("BUTTON_EDIT_PROFILE", properties);
};


// Payment Events
export const trackPaymentInitiated = (properties: {
  trip_id: string;
  trip_type: string;
  payment_type: string;
  amount: number;
  currency: string;
  vehicle_class: string;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("PAYMENT_INITIATED", properties);
};

export const trackPaymentMethodSelected = (properties: {
  trip_id: string;
  payment_method: string;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("PAYMENT_METHOD_SELECTED", properties);
};

export const trackPaymentProcessing = (properties: {
  trip_id: string;
  payment_method: string;
  payment_intent_id: string;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("PAYMENT_PROCESSING", properties);
};

export const trackPaymentSuccess = (properties: {
  trip_id: string;
  payment_method: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("PAYMENT_SUCCESS", properties);
};

export const trackPaymentFailed = (properties: {
  trip_id: string;
  payment_method: string;
  payment_intent_id?: string;
  error_code: string;
  error_message: string;
  timestamp: number;
  user_id: string;
}) => {
  mixpanel.track("PAYMENT_FAILED", properties);
};

// User Identification
export const identifyUser = (
  userId: string,
  userProperties: Record<string, any>
) => {
  mixpanel.identify(userId);
  mixpanel.people.set(userProperties);
};

// Reset User
export const resetUser = () => {
  mixpanel.reset();
};

type ExternalBookingProduct = "limousine" | "delivery";

/** 1. Partner tab landing — ?tab=0 or ?tab=1 only (no src=internal). UTM optional. */
export const trackExternalUtmSource = (properties: {
  landing_session_id: string;
  external_source: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_host: string | null;
  referrer_url: string | null;
  landing_tab: string | null;
  landing_product: ExternalBookingProduct | null;
  language: string;
  landing_path: string;
  timestamp: number;
}) => {
  mixpanel.track("extneral_utm_soruce", properties);
};

/** 2. External user created a booking (limousine or delivery) */
export const trackAddedBookingExternal = (properties: {
  landing_session_id: string;
  external_source: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_host: string | null;
  landing_tab: string | null;
  landing_product: ExternalBookingProduct | null;
  booking_product: ExternalBookingProduct;
  language: string;
  booking_id: string;
  booking_number?: string | number;
  product: ExternalBookingProduct;
  screen: string;
  timestamp: number;
  user_id?: string;
}) => {
  mixpanel.track("added_b_extneral_utm_soruce", properties);
};

/** 3. External user reached payment success screen */
export const trackSuccessfullyBookingExternal = (properties: {
  landing_session_id: string;
  external_source: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_host: string | null;
  landing_tab: string | null;
  landing_product: ExternalBookingProduct | null;
  booking_product: ExternalBookingProduct;
  language: string;
  booking_id: string;
  booking_number?: string | number;
  product: ExternalBookingProduct;
  screen: string;
  timestamp: number;
  user_id?: string;
}) => {
  mixpanel.track("successfully_b_extneral_utm_soruce", properties);
};
